import { Typography, Checkbox, FormControlLabel } from '@mui/material'
import { Link } from 'react-router-dom'
import {
  textOperators,
  equalityOperators,
  multiselectOperators,
  dateOperators
} from '../../../utils/constants/operators'
import { buildStatusColumn } from '../../fields/StatusField'

export const getContractColumns = ({ statusList = [], customers = [] }) => {
  const customerOptions = customers.map((item) => ({
    name: item.id.toString(),
    label: item.customer_name
  }))

  return [
    {
      name: 'contract_number',
      label: 'N° Contrat',
      isFilter: true,
      operators: textOperators
    },
    {
      name: 'customer_id',
      label: 'Client',
      isFilter: true,
      valueEditorType: 'multiselect',
      values: customerOptions,
      mapping: (id) => {
        const customer = customers.find((c) => c.id === id)
        return customer ? (
          <Typography
            component={Link}
            to='/customers/view'
            state={{ id, history: '/contracts' }}
            sx={{ textDecoration: 'none' }}
          >
            {customer.customer_name}
          </Typography>
        ) : (
          id
        )
      },
      operators: [...equalityOperators, ...multiselectOperators]
    },
    buildStatusColumn('contracts', statusList),
    {
      name: 'contract_date',
      label: 'Date Contrat',
      isFilter: true,
      inputType: 'date',
      type: 'date',
      format: 'DD/MM/YYYY',
      operators: dateOperators
    },
    {
      name: 'createdDateTime',
      label: 'Date Création',
      isFilter: true,
      inputType: 'datetime-local',
      type: 'date',
      format: 'DD/MM/YYYY HH:mm:ss',
      operators: dateOperators
    },
    {
      name: 'createdBy',
      label: 'Créé Par',
      isFilter: true,
      operators: textOperators
    },
    {
      name: 'lastModDateTime',
      label: 'Date Modification',
      isFilter: true,
      inputType: 'datetime-local',
      type: 'date',
      format: 'DD/MM/YYYY HH:mm:ss',
      operators: dateOperators
    },
    {
      name: 'lastModBy',
      label: 'Modifié Par',
      isFilter: true,
      operators: textOperators
    }
  ]
}

export const getContractFields = (user, contract, modulesList, setLines) => {
  const safeModulesList = Array.isArray(modulesList) ? modulesList : []

  const getModuleNameById = (id) => safeModulesList.find((m) => m.id === id)?.module || id

  const required = (msg) => [{ rule: 'req', error: msg }]

  return [
    [
      {
        name: 'company_id',
        label: 'Société',
        required: true,
        type: 'select',
        depends: 'customer_id',
        options: {
          type: 'api',
          object: 'companies',
          value: 'name',
          returnField: 'id',
          returnFields: ['id']
        },
        sizeDivided: 2,
        fullWidth: true,
        validation: required('Société obligatoire')
      },
      {
        name: 'customer_id',
        label: 'Client',
        required: true,
        type: 'select',
        depends: 'company_id',
        options: {
          type: 'api',
          object: 'customers',
          value: 'customer_name',
          returnField: 'id',
          returnFields: ['id', 'company_id'],
          filter: (data) => ({ company_id: data.company_id, owner_id: 1 })
        },
        sizeDivided: 2,
        fullWidth: true,
        validation: required('Client obligatoire')
      }
    ],
    [
      {
        name: 'contract_number',
        label: 'N° Contrat',
        readOnly: true,
        type: 'text',
        fullWidth: true,
        validationWhen: () => false,
        validation: required('N° Contrat obligatoire')
      },
      {
        name: 'contract_date',
        label: 'Date',
        required: true,
        type: 'date',
        fullWidth: true,
        validation: required('Date obligatoire')
      },
      {
        name: 'status',
        label: 'Statut',
        type: 'select',
        options: {
          type: 'api',
          object: 'status',
          value: 'status',
          returnField: 'status_code',
          returnFields: ['status_code'],
          filter: () => ({ object: 'contracts' })
        },
        sizeDivided: 2,
        fullWidth: true
      }
    ],
    [
      {
        name: 'contract_start_date',
        label: 'Date début',
        required: true,
        type: 'date',
        fullWidth: true,
        validation: required('Date début obligatoire')
      },
      {
        name: 'contract_end_date',
        label: 'Date fin',
        type: 'date',
        fullWidth: true
      }
    ],
    [
      {
        name: 'lines',
        label: 'Lignes',
        type: 'mytable',
        tableOptions: {
          paperStyle: { width: '100%', margin: '0 auto' },
          setDataParent: setLines,
          columns: [
            {
              name: 'module_id',
              label: 'Module',
              type: 'listSelect',
              mapping: (value) => getModuleNameById(value),
              onChangeF: (row, modules, onRowDataChange) => {
                const safeModules = Array.isArray(modules) ? modules : []
                const module = safeModules.find((m) => m.id === row.module_id)
                const updated = {
                  ...row,
                  is_quantity: module?.is_quantity ?? false,
                  module_id: module?.id ?? row.module_id,
                  is_access: row.is_access
                }
                onRowDataChange(updated)
              },
              optionsAPI: {
                api: 'modules',
                filter: () => ({})
              },
              dataId: 'module_id',
              optionLabel: 'module',
              returnField: 'id',
              enableEditing: true,
              validation: required('Module obligatoire')
            },
            {
              name: 'is_access',
              label: 'Accès',
              type: 'checkbox'
            },
            {
              name: 'is_quantity',
              label: 'Limiter quantité',
              type: 'checkbox',
              render: (props, onRowDataChange) => (
                <FormControlLabel control={<Checkbox checked={props.is_quantity || false} disabled />} />
              )
            },
            {
              name: 'quantity',
              label: 'Quantité',
              type: 'float',
              enableEditing: true,
              readOnlyWhen: (props) => {
                return !props.is_quantity
              },
              validationWhen: (props) => !!props.is_quantity,
              validation: required('Quantité obligatoire')
            }
          ],
          actions: {
            add: true,
            edit: true,
            editDisabled: (contract) => contract.status >= 90,
            delete: { message: 'Voulez-vous vraiment supprimer la ligne ?' }
          },
          title: 'Lignes',
          options: {
            pageSize: 10,
            search: false,
            paging: false,
            columnsButton: true,
            actionsColumnIndex: -1
          }
        },
        validation: required('Saisir au moins 1 ligne')
      }
    ]
  ]
}
